import React from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SpinnerIcon, ErrorIcon } from "@/components/icons";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
  isLoading?: boolean;
  children?: React.ReactNode;
  confirmDisabled?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "warning",
  isLoading = false,
  children,
  confirmDisabled = false,
}) => {
  const variantStyles = {
    danger: {
      iconBg: "bg-red-100",
      iconColor: "text-red-600",
      buttonVariant: "destructive" as const,
    },
    warning: {
      iconBg: "bg-yellow-100",
      iconColor: "text-yellow-600",
      buttonVariant: "primary" as const,
    },
    info: {
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      buttonVariant: "primary" as const,
    },
  };

  const styles = variantStyles[variant];

  const headerContent = (
    <div className="flex items-center">
      <div className={`w-10 h-10 ${styles.iconBg} rounded-full flex items-center justify-center mr-3`}>
        <ErrorIcon className={`w-5 h-5 ${styles.iconColor}`} />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
    </div>
  );

  const footerContent = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isLoading}>
        {cancelText}
      </Button>
      <Button
        variant={styles.buttonVariant}
        onClick={onConfirm}
        disabled={isLoading || confirmDisabled}
      >
        {isLoading ? (
          <>
            <SpinnerIcon className="-ml-1 mr-2 h-4 w-4" />
            Processing...
          </>
        ) : (
          confirmText
        )}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={headerContent}
      footer={footerContent}
      size="sm"
    >
      <div className="p-6">
        <p className="text-gray-700">{message}</p>
        {children}
      </div>
    </Modal>
  );
};
